import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./mitotraslados.css";

const API_URL =
  process.env.REACT_APP_MITO_SERVICIOS_URL ||
  "https://api.medic.com.ar/api/servicios/obtenerserviciosfinalizadosgraficos";

const PREFERRED_ORDER = [
  "Laferrere",
  "San Justo",
  "Caseros",
  "Moron",
  "Casanova",
  "Ramos Mejia",
  "Visitas 24hs",
  "Apoyo (N)",
];

const SLOT_MINUTES = 10;
const DAY_MINUTES = 24 * 60;
const SLOT_HEIGHT = 24;

const COLOR_LIST = [
  { label: "Rojo", class: "red", color: "red" },
  { label: "Amarillo", class: "yellow", color: "#eab308" },
  { label: "Verde", class: "green", color: "#22c55e" },
  { label: "Celeste", class: "celeste", color: "#0099ff" },
  { label: "Azul", class: "blue", color: "blue" },
  { label: "Fucsia", class: "fucsia", color: "#dd0795" },
  { label: "Naranja", class: "orange", color: "orange" },
  { label: "Negro", class: "dark", color: "#334155" },
];

function pad(value) {
  return String(value).padStart(2, "0");
}

function parseApiDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;

  const str = String(value).trim();
  const isoMatch = str.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/
  );

  if (isoMatch) {
    const [, y, m, d, hh, mm, ss = "00"] = isoMatch;
    return new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss)
    );
  }

  return new Date(str);
}

function formatApiDate(dateValue) {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function formatDayLabel(date) {
  return date.toLocaleDateString("es-AR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDayKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

function formatHourMinute(dateLike) {
  const d = new Date(dateLike);
  return Number.isNaN(d.getTime())
    ? "--:--"
    : `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function colorToClass(colorRaw) {
  const color = String(colorRaw || "").toUpperCase();
  if (color.includes("ROJO")) return "red";
  if (color.includes("FUCCIA") || color.includes("FUCSIA")) return "fucsia";
  if (color.includes("AMARILLO")) return "yellow";
  if (color.includes("VERDE CLARO")) return "light-green";
  if (color.includes("VERDE")) return "green";
  if (color.includes("NEGRO")) return "dark";
  if (color.includes("CELESTE")) return "celeste";
  if (color.includes("AZUL")) return "blue";
  if (color.includes("NARANJA")) return "orange";
  return "default";
}

function normalizeBaseName(baseRaw = "") {
  const b = String(baseRaw).toUpperCase().trim();
  if (!b) return "SIN BASE";
  if (b.includes("LAFERRERE")) return "Laferrere";
  if (b.includes("SAN JUSTO")) return "San Justo";
  if (b.includes("CASEROS")) return "Caseros";
  if (b.includes("MORON") || b.includes("MORÓN")) return "Moron";
  if (b.includes("CASANOVA")) return "Casanova";
  if (b.includes("RAMOS")) return "Ramos Mejia";
  if (b.includes("VISITAS")) return "Visitas 24hs";
  if (b.includes("APOYO")) return "Apoyo (N)";
  if (b.includes("UTIM")) return "UTIM 16 HS";
  return b;
}

function getNested(obj, path) {
  try {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  } catch {
    return null;
  }
}

function normalizeRecord(item, index) {
  const start = parseApiDate(
    item.fpasado || item.finicio || item.desde || item.fechaInicio
  );
  const end = parseApiDate(
    item.ffin || item.fhasta || item.hasta || item.fechaFin
  );
  const baseRaw =
    getNested(item, "tripulaciones.bases.nombre") ||
    item.base ||
    item.base_nombre ||
    "";
  const resolvedBase = normalizeBaseName(baseRaw);
  const color = item.color || "SIN COLOR";

  return {
    id: item.id || `rec-${index}`,
    start,
    end,
    color,
    colorClass: colorToClass(color),
    locality: item.localidad || "-",
    base: resolvedBase,
    baseRaw,
    mobile:
      getNested(item, "tripulaciones.movilId") ||
      item.movilId ||
      item.movil ||
      "-",
  };
}

function splitRecordIntoSegments(record) {
  if (!record.start || !record.end || record.end <= record.start) return [];

  const segments = [];
  let cursor = new Date(record.start);

  while (cursor < record.end) {
    const dayStart = new Date(
      cursor.getFullYear(),
      cursor.getMonth(),
      cursor.getDate(),
      0,
      0,
      0
    );

    const nextDay = new Date(dayStart);
    nextDay.setDate(nextDay.getDate() + 1);

    const segmentEnd = record.end < nextDay ? record.end : nextDay;

    segments.push({
      ...record,
      dayKey: formatDayKey(dayStart),
      dayDate: dayStart,
      segmentStart: new Date(cursor),
      segmentEnd: new Date(segmentEnd),
    });

    cursor = segmentEnd;
  }

  return segments;
}

function assignLanes(items) {
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin);
  const lanes = [];

  const result = sorted.map((item) => {
    let laneIndex = lanes.findIndex((l) => item.startMin >= l);

    if (laneIndex === -1) {
      laneIndex = lanes.length;
      lanes.push(item.endMin);
    } else {
      lanes[laneIndex] = item.endMin;
    }

    return { ...item, laneIndex };
  });

  return result.map((item) => ({
    ...item,
    totalLanes: Math.max(1, lanes.length),
  }));
}

function buildBarsForDay(recordsForDay, dynamicColumns) {
  const grouped = {};
  dynamicColumns.forEach((b) => {
    grouped[b] = [];
  });

  recordsForDay.forEach((rec) => {
    if (!grouped[rec.base]) return;

    const s = rec.segmentStart;
    const e = rec.segmentEnd;
    const startMin = s.getHours() * 60 + s.getMinutes();
    let endMin = e.getHours() * 60 + e.getMinutes();

    if (endMin === 0 && e > s) endMin = DAY_MINUTES;

    grouped[rec.base].push({
      ...rec,
      startMin,
      endMin,
      topPx: startMin * (SLOT_HEIGHT / SLOT_MINUTES),
      heightPx:
        Math.max(endMin - startMin, SLOT_HEIGHT) * (SLOT_HEIGHT / SLOT_MINUTES),
    });
  });

  const final = {};

  dynamicColumns.forEach((b) => {
    const sorted = grouped[b].sort((x, y) => x.startMin - y.startMin);

    const withWaiting = sorted.map((bar, idx) => {
      let waitingTime = null;
      let gapTopPx = null;
      let gapHeightPx = null;

      if (idx > 0) {
        const prev = sorted[idx - 1];
        const diff = bar.startMin - prev.endMin;

        if (diff > 0) {
          waitingTime = diff;
          gapTopPx = prev.endMin * (SLOT_HEIGHT / SLOT_MINUTES);
          gapHeightPx = diff * (SLOT_HEIGHT / SLOT_MINUTES);
        }
      }

      return {
        ...bar,
        waitingTime,
        gapTopPx,
        gapHeightPx,
      };
    });

    final[b] = assignLanes(withWaiting).map((bar) => ({
      ...bar,
      left: (bar.laneIndex * 100) / bar.totalLanes,
      width: 100 / bar.totalLanes,
    }));
  });

  return final;
}

function orderBasesWithPriority(foundBases, preferredOrder) {
  const preferred = preferredOrder.filter((base) => foundBases.includes(base));
  const others = foundBases
    .filter((base) => !preferredOrder.includes(base))
    .sort((a, b) => a.localeCompare(b));

  return [...preferred, ...others];
}

export default function MitoTrasladosScreen() {
  const [desde, setDesde] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });

  const [hasta, setHasta] = useState(() => new Date().toISOString().slice(0, 16));
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedColors, setSelectedColors] = useState([]);

  const scrollRef = useRef(null);
  const graphExportRef = useRef(null);
  const tableWrapperRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);

    try {
      const body = {
        fdesde: formatApiDate(desde),
        fhasta: formatApiDate(hasta),
      };

      const res = await axios.post(API_URL, body);
      const normalized = (Array.isArray(res.data) ? res.data : []).map(normalizeRecord);

      setRecords(normalized);

      if (normalized.length > 0) {
        setSelectedDayKey(formatDayKey(new Date(desde)));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const segmented = useMemo(() => records.flatMap(splitRecordIntoSegments), [records]);

  const daysMap = useMemo(() => {
    const map = new Map();

    segmented.forEach((r) => {
      if (!map.has(r.dayKey)) {
        map.set(r.dayKey, {
          key: r.dayKey,
          label: formatDayLabel(r.dayDate),
          date: r.dayDate,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.date - b.date);
  }, [segmented]);

  const currentDayRecords = useMemo(() => {
    return segmented.filter((r) => {
      const matchSearch =
        search === "" ||
        JSON.stringify(r).toLowerCase().includes(search.toLowerCase());

      const matchColor =
        selectedColors.length === 0 ||
        selectedColors.some((c) => r.colorClass === c);

      return r.dayKey === selectedDayKey && matchSearch && matchColor;
    });
  }, [segmented, selectedDayKey, search, selectedColors]);

  useEffect(() => {
    if (search.length > 1 && currentDayRecords.length > 0) {
      const found = currentDayRecords.find(
        (r) =>
          r.mobile.toString().includes(search) ||
          r.locality.toLowerCase().includes(search.toLowerCase())
      );

      if (found && scrollRef.current) {
        const startMin =
          found.segmentStart.getHours() * 60 + found.segmentStart.getMinutes();
        const topPos = startMin * (SLOT_HEIGHT / SLOT_MINUTES);

        scrollRef.current.scrollTo({
          top: topPos - 100,
          behavior: "smooth",
        });
      }
    }
  }, [search, currentDayRecords]);

  const dynamicColumns = useMemo(() => {
    const foundBases = Array.from(new Set(currentDayRecords.map((r) => r.base)));
    const orderedFoundBases = orderBasesWithPriority(foundBases, PREFERRED_ORDER);

    if (selectedColors.length > 0) {
      return orderedFoundBases;
    }

    const preferredVisible = PREFERRED_ORDER.filter((base) =>
      foundBases.includes(base)
    );

    const otherVisible = foundBases
      .filter((base) => !PREFERRED_ORDER.includes(base))
      .sort((a, b) => a.localeCompare(b));

    return [...preferredVisible, ...otherVisible];
  }, [currentDayRecords, selectedColors]);

  const barsByBase = useMemo(
    () => buildBarsForDay(currentDayRecords, dynamicColumns),
    [currentDayRecords, dynamicColumns]
  );

  const countsByBase = useMemo(() => {
    const counts = {};
    dynamicColumns.forEach((base) => {
      counts[base] = barsByBase[base]?.length || 0;
    });
    return counts;
  }, [dynamicColumns, barsByBase]);

  const TIME_SLOTS = useMemo(() => {
    const slots = [];
    for (let m = 0; m < DAY_MINUTES; m += SLOT_MINUTES) {
      slots.push({
        index: m / SLOT_MINUTES,
        label: `${pad(Math.floor(m / 60))}:${pad(m % 60)}`,
      });
    }
    return slots;
  }, []);

  const selectedDayLabel = useMemo(() => {
    const found = daysMap.find((d) => d.key === selectedDayKey);
    return found?.label || "sin-fecha";
  }, [daysMap, selectedDayKey]);

  const toggleColorFilter = (colorClass) => {
    setSelectedColors((prev) =>
      prev.includes(colorClass)
        ? prev.filter((c) => c !== colorClass)
        : [...prev, colorClass]
    );
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleDownloadPdf = async () => {
    if (!graphExportRef.current || !tableWrapperRef.current) return;

    let cloneHost = null;

    try {
      setDownloadingPdf(true);

      const originalSection = graphExportRef.current;
      const originalWrapper = tableWrapperRef.current;
      const fullWidth = originalWrapper.scrollWidth;
      const fullHeight = originalWrapper.scrollHeight;

      cloneHost = document.createElement("div");
      cloneHost.style.position = "fixed";
      cloneHost.style.left = "-100000px";
      cloneHost.style.top = "0";
      cloneHost.style.width = `${fullWidth + 80}px`;
      cloneHost.style.background = "#ffffff";
      cloneHost.style.padding = "24px";
      cloneHost.style.margin = "0";
      cloneHost.style.zIndex = "-1";
      cloneHost.style.overflow = "visible";

      const clone = originalSection.cloneNode(true);
      clone.style.width = `${fullWidth}px`;
      clone.style.maxWidth = "none";
      clone.style.minWidth = `${fullWidth}px`;
      clone.style.overflow = "visible";
      clone.style.height = "auto";
      clone.style.background = "#ffffff";

      const cloneWrapper = clone.querySelector(".graphic-table-wrapper");
      if (cloneWrapper) {
        cloneWrapper.style.width = `${fullWidth}px`;
        cloneWrapper.style.maxWidth = "none";
        cloneWrapper.style.minWidth = `${fullWidth}px`;
        cloneWrapper.style.maxHeight = "none";
        cloneWrapper.style.height = `${fullHeight}px`;
        cloneWrapper.style.overflow = "visible";
      }

      const cloneTable = clone.querySelector(".graphic-table");
      if (cloneTable) {
        cloneTable.style.width = `${fullWidth}px`;
        cloneTable.style.minWidth = `${fullWidth}px`;
        cloneTable.style.maxWidth = "none";
      }

      const stickyHeaders = clone.querySelectorAll(".graphic-table thead th");
      stickyHeaders.forEach((el) => {
        el.style.position = "static";
        el.style.top = "auto";
        el.style.left = "auto";
      });

      const stickyTimeCells = clone.querySelectorAll(".time-column, .time-cells");
      stickyTimeCells.forEach((el) => {
        el.style.position = "static";
        el.style.left = "auto";
      });

      cloneHost.appendChild(clone);
      document.body.appendChild(cloneHost);

      await new Promise((resolve) => setTimeout(resolve, 150));

      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: 0,
        windowWidth: fullWidth + 100,
        windowHeight: clone.scrollHeight + 100,
      });

      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      const pxPerMm = canvas.width / usableWidth;
      const pageCanvasHeight = Math.floor(usableHeight * pxPerMm);

      let renderedHeight = 0;
      let pageIndex = 0;

      while (renderedHeight < canvas.height) {
        const sliceHeight = Math.min(pageCanvasHeight, canvas.height - renderedHeight);

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;

        const ctx = pageCanvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(
            canvas,
            0,
            renderedHeight,
            canvas.width,
            sliceHeight,
            0,
            0,
            canvas.width,
            sliceHeight
          );
        }

        const pageImgData = pageCanvas.toDataURL("image/png");
        const renderHeightMm = sliceHeight / pxPerMm;

        if (pageIndex > 0) {
          pdf.addPage();
        }

        pdf.addImage(
          pageImgData,
          "PNG",
          margin,
          margin,
          usableWidth,
          renderHeightMm
        );

        renderedHeight += sliceHeight;
        pageIndex += 1;
      }

      const safeLabel = selectedDayLabel
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "_");

      pdf.save(`libro_grafico_${safeLabel}.pdf`);
    } catch (error) {
      console.error("Error al generar PDF:", error);
    } finally {
      if (cloneHost && cloneHost.parentNode) {
        cloneHost.parentNode.removeChild(cloneHost);
      }
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="mito-page">
      <div className="toolbar">
        <div className="toolbar-field">
          <label>Desde</label>
          <input
            type="datetime-local"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </div>

        <div className="toolbar-field">
          <label>Hasta</label>
          <input
            type="datetime-local"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </div>

        <div className="toolbar-field toolbar-search">
          <label>Barra Inteligente</label>
          <input
            type="text"
            placeholder="móvil, localidad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="toolbar-field">
          <label>Filtrar Colores</label>
          <div className="color-filter-container">
            {COLOR_LIST.map((c) => (
              <button
                key={c.class}
                type="button"
                className={`color-pill ${
                  selectedColors.includes(c.class) ? "active" : ""
                }`}
                style={{ "--c": c.color }}
                onClick={() => toggleColorFilter(c.class)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="toolbar-actions">
          <button
            type="button"
            className="toolbar-btn btn-secondary"
            onClick={handleGoBack}
          >
            ← Volver
          </button>

          <button
            type="button"
            className="toolbar-btn btn-outline"
            onClick={fetchData}
          >
            {loading ? "Actualizando..." : "⟳ Actualizar"}
          </button>
        </div>
      </div>

      <section className="graph-section" ref={graphExportRef}>
        <div className="section-top">
          <div>
            <h2>Libro gráfico de servicios finalizados</h2>
            <p className="total-label">Total: {currentDayRecords.length} servicios</p>
          </div>

          <div className="section-actions">
            <button
              type="button"
              className="toolbar-btn btn-pdf"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              {downloadingPdf ? "Generando PDF..." : "⬇ Descargar PDF"}
            </button>

            {daysMap.length > 1 && (
              <select
                value={selectedDayKey || ""}
                onChange={(e) => setSelectedDayKey(e.target.value)}
                className="day-select"
              >
                {daysMap.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="graphic-table-wrapper" ref={tableWrapperRef}>
          <table className="graphic-table">
            <thead>
              <tr>
                <th className="time-column">Horarios</th>
                {dynamicColumns.map((base) => (
                  <th key={base}>
                    <div className="base-header-content">
                      <span className="base-header-name">{base}</span>
                      <span className="base-header-count">{countsByBase[base] || 0}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              <tr className="bars-row">
                <td className="time-cells">
                  {TIME_SLOTS.map((s) => (
                    <div key={s.index} className="time-slot">
                      {s.label}
                    </div>
                  ))}
                </td>

                {dynamicColumns.map((base) => (
                  <td key={base} className="base-column-cell">
                    <div
                      className="base-column"
                      style={{
                        height: DAY_MINUTES * (SLOT_HEIGHT / SLOT_MINUTES) + "px",
                      }}
                    >
                      {TIME_SLOTS.map((s) => (
                        <div key={s.index} className="grid-slot" />
                      ))}

                      {barsByBase[base]?.map((bar) => (
                        <React.Fragment
                          key={`${bar.id}-${bar.segmentStart?.getTime?.() || Math.random()}`}
                        >
                          {bar.waitingTime &&
                            bar.waitingTime > 0 &&
                            bar.laneIndex === 0 &&
                            bar.gapHeightPx > 14 && (
                              <div
                                className="gap-indicator"
                                style={{
                                  top: `${bar.gapTopPx + 2}px`,
                                  height: `${Math.max(bar.gapHeightPx - 4, 10)}px`,
                                }}
                              >
                                <div className="gap-indicator-line" />
                                <span className="gap-indicator-label">
                                  ⌛ {bar.waitingTime} min transcurridos
                                </span>
                              </div>
                            )}

                          <button
                            type="button"
                            className={`service-bar bar-${bar.colorClass}`}
                            style={{
                              top: `${bar.topPx}px`,
                              height: `${bar.heightPx}px`,
                              left: `calc(${bar.left}% + 6px)`,
                              width: `calc(${bar.width}% - 12px)`,
                            }}
                            onClick={() => setSelectedService(bar)}
                          >
                            <div className="service-bar-inner">
                              <span className="service-time">
                                {formatHourMinute(bar.segmentStart)} -{" "}
                                {formatHourMinute(bar.segmentEnd)}
                              </span>
                              <span className="service-mobile">
                                Móvil: {bar.mobile}
                              </span>
                              <span className="service-id">{bar.locality}</span>
                            </div>
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {selectedService && (
        <div className="modal-backdrop" onClick={() => setSelectedService(null)}>
          <div className="service-modal" onClick={(e) => e.stopPropagation()}>
            <div className="service-modal-header">
              <h3>Detalle del servicio</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setSelectedService(null)}
              >
                ×
              </button>
            </div>

            <div className="service-modal-body">
              <div className="modal-field">
                <span className="modal-label">ID SERVICIO</span>
                <span className="modal-value">#{selectedService.id}</span>
              </div>

              <div className="modal-field">
                <span className="modal-label">Horario Real</span>
                <span className="modal-value">
                  {formatHourMinute(selectedService.start)} -{" "}
                  {formatHourMinute(selectedService.end)}
                </span>
              </div>

              <div className="modal-field">
                <span className="modal-label">Color</span>
                <span
                  className="modal-value"
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <i className={`legend-dot dot-${selectedService.colorClass}`} />
                  {selectedService.color}
                </span>
              </div>

              <div className="modal-field">
                <span className="modal-label">Móvil</span>
                <span className="modal-value">{selectedService.mobile}</span>
              </div>

              <div className="modal-field">
                <span className="modal-label">Localidad</span>
                <span className="modal-value">{selectedService.locality}</span>
              </div>

              <div className="modal-field">
                <span className="modal-label">Base / Destino</span>
                <span className="modal-value">{selectedService.baseRaw}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
